"use client";

import React, { Suspense, useEffect, useState } from "react";
import Image from "next/image";
import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { message } from "antd";
import { deleteCookie } from "cookies-next";
import { Link, usePathname } from "@/navigation";
import { SITE_LOCALES } from "@/lib/constant";
import { contactInfo } from "@/lib/contacts";
import { useUserStore } from "@/store/user.store";
import { useUserAuth } from "@/lib/hooks/useUserAuth";
import { useRouter } from "@/navigation";
import FlagIcon from "@/components/Flag";
import LoginModal from "@/components/User/login-modal";

export type MobileMenuSubItem = {
	key: string | number;
	href: string;
	label: string;
};

export type MobileMenuItem = {
	id: number;
	label: string;
	href: string;
	isLinkable: boolean;
	isActive: boolean;
	subItems: MobileMenuSubItem[];
};

type MobileMenuDrawerProps = {
	isOpen: boolean;
	onClose: () => void;
	menuItems: MobileMenuItem[];
	inquiryCount: number;
};

const WHATSAPP_LINK = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, "")}`;

type LocaleSwitchLinkProps = {
	targetLocale: string;
	className: string;
	onClick: () => void;
	children: React.ReactNode;
};

const LocaleSwitchLinkWithQuery = ({ targetLocale, className, onClick, children }: LocaleSwitchLinkProps) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const queryString = searchParams?.toString();
	const currentPathWithQuery = queryString ? `${pathname}?${queryString}` : pathname;

	return (
		<Link href={currentPathWithQuery} locale={targetLocale} onClick={onClick} className={className}>
			{children}
		</Link>
	);
};

// useSearchParams 在静态预渲染时必须处于 Suspense 边界内，否则 next build 会失败
const LocaleSwitchLink = (props: LocaleSwitchLinkProps) => {
	const pathname = usePathname();
	return (
		<Suspense
			fallback={
				<Link href={pathname} locale={props.targetLocale} onClick={props.onClick} className={props.className}>
					{props.children}
				</Link>
			}
		>
			<LocaleSwitchLinkWithQuery {...props} />
		</Suspense>
	);
};

export default function MobileMenuDrawer({ isOpen, onClose, menuItems, inquiryCount }: MobileMenuDrawerProps) {
	const t = useTranslations();
	const locale = useLocale();
	const router = useRouter();
	const { userInfo, getUserInfo } = useUserStore();
	const { useSignOut } = useUserAuth();

	const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null);
	const [isLanguagePanelOpen, setIsLanguagePanelOpen] = useState(false);
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
	const [loginModalActiveId, setLoginModalActiveId] = useState(1);
	const [hasMounted, setHasMounted] = useState(false);

	const activeLocale = SITE_LOCALES.find((localeItem) => localeItem.code === locale);
	const isI18nEnabled = process.env.NEXT_PUBLIC_IS_I18N === "true";

	useEffect(() => {
		setHasMounted(true);
		getUserInfo();
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setIsLanguagePanelOpen(false);
			return;
		}
		const activeMenuWithSubItems = menuItems.find((menuItem) => menuItem.isActive && menuItem.subItems.length > 0);
		setExpandedMenuId(activeMenuWithSubItems?.id ?? null);
	}, [isOpen]);

	useEffect(() => {
		const previousOverflow = document.documentElement.style.overflow;
		if (isOpen) {
			document.documentElement.style.overflow = "hidden";
		}
		return () => {
			document.documentElement.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};
		window.addEventListener("keydown", handleEscapeKey);
		return () => window.removeEventListener("keydown", handleEscapeKey);
	}, [isOpen, onClose]);

	const toggleExpandedMenu = (menuId: number) => {
		setExpandedMenuId((currentExpandedMenuId) => (currentExpandedMenuId === menuId ? null : menuId));
	};

	const handleAccountAction = () => {
		if (userInfo) {
			deleteCookie("__user__login__info");
			deleteCookie("user-store");
			useSignOut();
			router.push("/");
			onClose();
			message.success(t("message.logout successful"));
			return;
		}
		onClose();
		setLoginModalActiveId(1);
		setIsLoginModalOpen(true);
	};

	const contactShortcuts = [
		{ key: "phone", href: `tel:${contactInfo.phone.replace(/\s/g, "")}`, icon: "ri-phone-line", label: t("common.Phone") },
		{ key: "email", href: `mailto:${contactInfo.email}`, icon: "ri-mail-line", label: t("common.Email") },
		{ key: "whatsapp", href: WHATSAPP_LINK, icon: "ri-whatsapp-line", label: "WhatsApp" },
	];

	return (
		<>
			<div
				aria-hidden="true"
				onClick={onClose}
				className={clsx(
					"fixed inset-0 z-[110] bg-slate-900/50 backdrop-blur-[2px] transition-opacity duration-300",
					isOpen ? "opacity-100" : "pointer-events-none opacity-0"
				)}
			/>

			<aside
				role="dialog"
				aria-modal="true"
				aria-label={t("nav.MENU")}
				aria-hidden={!isOpen}
				className={clsx(
					"fixed inset-y-0 left-0 z-[111] flex h-[100dvh] w-[86vw] max-w-[380px] flex-col bg-white shadow-2xl transition-[transform,visibility] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
					isOpen ? "visible translate-x-0" : "invisible -translate-x-full"
				)}
			>
				<div className="flex h-16 shrink-0 items-center justify-between border-b border-gray-100 px-5">
					<Link href="/" onClick={onClose} aria-label="BLINCE" className="flex items-center">
						<Image src="/image/plsyy/logo.png" width={1633} height={345} alt="BLINCE" className="h-6 w-auto" />
					</Link>
					<button
						type="button"
						onClick={onClose}
						aria-label={t("nav.close")}
						className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-[22px] text-gray-600 transition-colors hover:bg-gray-100 active:bg-gray-200"
					>
						<i className="ri-close-line" aria-hidden="true" />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto overscroll-contain">
					<nav aria-label="Mobile" className="px-3 py-3">
						<ul className="space-y-0.5">
							{menuItems.map((menuItem, menuIndex) => {
								const hasSubItems = menuItem.subItems.length > 0;
								const isExpanded = expandedMenuId === menuItem.id;
								const rowClassName = clsx(
									"flex min-h-[48px] flex-1 items-center rounded-lg px-3 text-[15px] font-medium transition-colors",
									menuItem.isActive ? "text-mainColor" : "text-gray-800"
								);

								return (
									<li
										key={menuItem.id}
										className={clsx(
											"transition-all duration-300",
											isOpen ? "translate-x-0 opacity-100" : "-translate-x-3 opacity-0"
										)}
										style={{ transitionDelay: isOpen ? `${80 + menuIndex * 35}ms` : "0ms" }}
									>
										<div
											className={clsx(
												"flex items-center rounded-lg",
												menuItem.isActive ? "bg-mainColor/[0.06]" : "active:bg-gray-50"
											)}
										>
											{menuItem.isLinkable ? (
												<Link href={menuItem.href} onClick={onClose} className={rowClassName}>
													{menuItem.isActive && <span className="mr-2.5 h-4 w-[3px] rounded-full bg-mainColor" aria-hidden="true" />}
													{menuItem.label}
												</Link>
											) : (
												<button
													type="button"
													onClick={() => hasSubItems && toggleExpandedMenu(menuItem.id)}
													className={clsx(rowClassName, "text-left")}
												>
													{menuItem.isActive && <span className="mr-2.5 h-4 w-[3px] rounded-full bg-mainColor" aria-hidden="true" />}
													{menuItem.label}
												</button>
											)}

											{hasSubItems && (
												<button
													type="button"
													onClick={() => toggleExpandedMenu(menuItem.id)}
													aria-expanded={isExpanded}
													aria-label={menuItem.label}
													className={clsx(
														"mr-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl transition-colors",
														isExpanded ? "bg-mainColor/10 text-mainColor" : "text-gray-400 active:bg-gray-100"
													)}
												>
													<i
														className={clsx(
															"ri-arrow-down-s-line transition-transform duration-300",
															isExpanded && "rotate-180"
														)}
														aria-hidden="true"
													/>
												</button>
											)}
										</div>

										{hasSubItems && (
											<div
												className={clsx(
													"grid transition-[grid-template-rows] duration-300 ease-out",
													isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
												)}
											>
												<div className="overflow-hidden">
													<ul className="ml-6 border-l border-gray-200 py-1 pl-3">
														{menuItem.subItems.map((subItem) => (
															<li key={subItem.key}>
																<Link
																	href={subItem.href}
																	onClick={onClose}
																	className="flex min-h-[40px] items-center rounded-md px-3 text-[14px] text-gray-600 transition-colors active:bg-gray-50 active:text-mainColor"
																>
																	{subItem.label}
																</Link>
															</li>
														))}
													</ul>
												</div>
											</div>
										)}
									</li>
								);
							})}
						</ul>
					</nav>

					<div
						className={clsx(
							"mx-5 border-t border-gray-100 pb-6 pt-5 transition-opacity duration-500",
							isOpen ? "opacity-100 delay-300" : "opacity-0"
						)}
					>
						<Link
							href="/inquiry"
							onClick={onClose}
							className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-mainColor text-[15px] font-medium text-white shadow-sm transition-colors active:bg-mainColor/90"
						>
							<i className="ri-file-list-3-line text-lg" aria-hidden="true" />
							<span>{t("nav.Inquiry")}</span>
							{inquiryCount > 0 && (
								<span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-semibold text-mainColor">
									{inquiryCount > 99 ? 99 : inquiryCount}
								</span>
							)}
						</Link>

						<div className="mt-4 grid grid-cols-3 gap-2">
							{contactShortcuts.map((contactShortcut) => (
								<a
									key={contactShortcut.key}
									href={contactShortcut.href}
									target={contactShortcut.key === "whatsapp" ? "_blank" : undefined}
									rel={contactShortcut.key === "whatsapp" ? "noopener noreferrer" : undefined}
									className="flex flex-col items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50/60 py-3 text-gray-600 transition-colors active:border-mainColor/30 active:bg-mainColor/5 active:text-mainColor"
								>
									<i className={clsx(contactShortcut.icon, "text-xl text-mainColor")} aria-hidden="true" />
									<span className="text-[12px]">{contactShortcut.label}</span>
								</a>
							))}
						</div>

						<p className="mt-4 flex items-start gap-2.5 text-[13px] leading-relaxed text-gray-500">
							<i className="ri-map-pin-line mt-0.5 text-[15px] text-gray-400" aria-hidden="true" />
							<span>{contactInfo.address}</span>
						</p>
					</div>
				</div>

				<div className="flex shrink-0 items-center gap-2 border-t border-gray-100 bg-white px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
					<button
						type="button"
						onClick={handleAccountAction}
						className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-lg px-3 text-[14px] font-medium text-gray-700 transition-colors active:bg-gray-100"
					>
						<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-base text-gray-600">
							<i className={userInfo ? "ri-logout-box-r-line" : "ri-user-3-line"} aria-hidden="true" />
						</span>
						<span className="truncate">
							{hasMounted ? (userInfo ? t("common.Logout") : t("common.Sign_in")) : ""}
						</span>
					</button>

					{isI18nEnabled && (
						<button
							type="button"
							onClick={() => setIsLanguagePanelOpen(true)}
							className="flex h-11 shrink-0 items-center gap-2 rounded-lg border border-gray-200 px-3 text-[13px] font-medium text-gray-700 transition-colors active:bg-gray-50"
							aria-haspopup="dialog"
							aria-expanded={isLanguagePanelOpen}
						>
							{activeLocale && <FlagIcon flag={activeLocale.flag} />}
							<span className="max-w-[96px] truncate">{activeLocale?.nativeName}</span>
							<i className="ri-arrow-up-s-line text-base text-gray-400" aria-hidden="true" />
						</button>
					)}
				</div>

				{isI18nEnabled && (
					<div
						className={clsx(
							"absolute inset-0 z-10 flex flex-col justify-end transition-opacity duration-200",
							isLanguagePanelOpen ? "opacity-100" : "pointer-events-none opacity-0"
						)}
					>
						<div aria-hidden="true" className="absolute inset-0 bg-black/30" onClick={() => setIsLanguagePanelOpen(false)} />
						<div
							role="dialog"
							aria-modal="true"
							className={clsx(
								"relative max-h-[70%] overflow-y-auto rounded-t-2xl bg-white pb-[max(12px,env(safe-area-inset-bottom))] shadow-2xl transition-transform duration-300",
								isLanguagePanelOpen ? "translate-y-0" : "translate-y-full"
							)}
						>
							<div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-5 py-3.5">
								<span className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
									<i className="ri-global-line text-lg text-mainColor" aria-hidden="true" />
									{activeLocale?.nativeName}
								</span>
								<button
									type="button"
									onClick={() => setIsLanguagePanelOpen(false)}
									aria-label={t("nav.close")}
									className="-mr-2 flex h-9 w-9 items-center justify-center rounded-full text-xl text-gray-500 active:bg-gray-100"
								>
									<i className="ri-close-line" aria-hidden="true" />
								</button>
							</div>
							<ul className="p-2">
								{SITE_LOCALES.map((localeItem) => {
									const isActiveLocale = localeItem.code === locale;
									return (
										<li key={localeItem.code}>
											<LocaleSwitchLink
												targetLocale={localeItem.code}
												onClick={() => {
													setIsLanguagePanelOpen(false);
													onClose();
												}}
												className={clsx(
													"flex min-h-[48px] items-center gap-3 rounded-lg px-3 text-[15px] transition-colors",
													isActiveLocale ? "bg-mainColor/[0.06] font-medium text-mainColor" : "text-gray-700 active:bg-gray-50"
												)}
											>
												<FlagIcon flag={localeItem.flag} />
												<span className="flex-1">{localeItem.nativeName}</span>
												{isActiveLocale && <i className="ri-check-line text-lg" aria-hidden="true" />}
											</LocaleSwitchLink>
										</li>
									);
								})}
							</ul>
						</div>
					</div>
				)}
			</aside>

			<LoginModal
				openModal={isLoginModalOpen}
				setOpenModal={setIsLoginModalOpen}
				activeId={loginModalActiveId}
				setActiveId={setLoginModalActiveId}
			/>
		</>
	);
}
